/**
 * `classifyResetRefusal(err, stage)` — what a refused password-reset request MEANS, and what the person can do next.
 *
 * The ONE rule every reset surface words its refusals by: login-shell's `<PasswordResetModal>` (every web app) and
 * palm-native's `ForgotPasswordScreen` (PALM-RESET-FLOW-001). It returns a REFUSAL NAME, never words: each surface maps
 * the name to its own catalog leaf, so the meaning is decided once and the copy stays where each catalog lives.
 *
 * Before this module the rule lived only in login-shell (`classifyResetError`), which palm-native cannot import (its
 * barrel carries web routing), so palm-native held its own copy — and the two had drifted: on a verify 429 the web sent
 * the person back to the email step, while palm left them on a code the server had already deleted.
 *
 * It reads only the status, three machine codes (the CAPTCHA gate's, the change recorder's, the validation gate's), the
 * server's own throttle window and the NAME of a field a validation refusal lists: the server's sentence is English,
 * and the reset routers answer enumeration-safely, so the class of failure is the whole signal
 * (`@puffin-core/auth-routes` `createPasswordResetRouter`).
 *
 * Pure, and React-free: it sits beside `./refusal` as its own subpath (`@puffin-core/auth-shell/reset-refusal`).
 *
 * @module @puffin-core/auth-shell/resetRefusal
 */

import { serverLockoutMs } from '@puffin-core/form-security/core';

/** The refusal names a reset surface maps to its catalog. */
export const RESET_REFUSALS = Object.freeze({
  NETWORK: 'network',
  CAPTCHA: 'captcha',
  NOT_RECORDED: 'notRecorded',
  CODE_LOCKED: 'codeLocked',
  TOO_SOON: 'tooSoon',
  RATE_LIMITED: 'rateLimited',
  CODE_INVALID: 'codeInvalid',
  SESSION_EXPIRED: 'sessionExpired',
  PASSWORD_REJECTED: 'passwordRejected',
  INVALID_FIELD: 'invalidField',
  FAILED: 'failed',
});

/** The reset steps a refusal can answer. */
export const RESET_STAGES = Object.freeze({
  INITIATE: 'initiate',
  VERIFY: 'verify',
  COMPLETE: 'complete',
  RESEND: 'resend',
});

const TOO_MANY_REQUESTS = 429;
const UNAUTHORIZED = 401;
const BAD_REQUEST = 400;
const UNPROCESSABLE = 422;

/** The validation gate's code (`@puffin-core/middleware` `validateBody`). */
const VALIDATION_ERROR = 'VALIDATION_ERROR';

/** The field the reset session lives in: a refused one is an expired session, whatever refused it. */
const RESET_TOKEN_FIELD = 'resetToken';

/**
 * The first field a validation refusal names. Its `details` are the value-free shape `@puffin-core/response-helpers`'
 * `formatJoiError` answers (`[{ field, rule, params, message }]`), so only the field's NAME is read.
 *
 * @param {*} details
 * @returns {?string}
 */
function firstRefusedField(details) {
  if (!Array.isArray(details)) return null;
  const named = details.find((detail) => typeof detail?.field === 'string' && detail.field !== '');
  return named ? named.field : null;
}

/**
 * @param {{status?: number, errorCode?: ?string, errorDetails?: *}|null|undefined} err - a rejection in the fleet's
 *   error vocabulary (`@puffin-core/api-client`'s normalized error, or login-shell's reset client's)
 * @param {'initiate'|'verify'|'complete'|'resend'} stage - the step that was refused
 * @returns {{refusal: string, backToEmail?: true, retryAfter?: number, field?: string}} the refusal's name;
 *   `backToEmail` when the only way on is a fresh code; `retryAfter` (whole seconds) when the server named its own
 *   window; `field` (an `invalidField` refusal) the request field the server refused, which each surface words
 */
export function classifyResetRefusal(err, stage) {
  const status = err?.status;
  if (status === undefined) return { refusal: RESET_REFUSALS.NETWORK };
  if (err.errorCode === 'CAPTCHA_FAILED') return { refusal: RESET_REFUSALS.CAPTCHA };
  // A reset is a RECORDED change, refused with nothing changed when its audit record cannot be kept (owner ruling
  // Q-EE-22 "Plain, retry"): the reset session is still good, so the person stays on the step. The code decides, at any
  // status.
  if (err.errorCode === 'AUDIT_RECORD_UNAVAILABLE') return { refusal: RESET_REFUSALS.NOT_RECORDED };
  if (status === TOO_MANY_REQUESTS) {
    // After too many wrong codes the router DELETES the code, and a resend only re-issues an EXISTING code — so it
    // would silently send nothing. The only way on is a fresh request.
    if (stage === RESET_STAGES.VERIFY) {
      return { refusal: RESET_REFUSALS.CODE_LOCKED, backToEmail: true };
    }
    // The server's lockout, by form-security's ONE rule: its throttle's code and a positive whole number of seconds.
    const lockoutMs = serverLockoutMs(err);
    if (lockoutMs === null) return { refusal: RESET_REFUSALS.RATE_LIMITED };
    return { refusal: RESET_REFUSALS.TOO_SOON, retryAfter: lockoutMs / 1000 };
  }
  if (stage === RESET_STAGES.VERIFY && status === UNAUTHORIZED) {
    return { refusal: RESET_REFUSALS.CODE_INVALID };
  }
  // The reset token expired or names no account that may reset: only a new code replaces it.
  if (stage === RESET_STAGES.COMPLETE && status === UNAUTHORIZED) {
    return { refusal: RESET_REFUSALS.SESSION_EXPIRED, backToEmail: true };
  }
  if (stage === RESET_STAGES.COMPLETE && status === BAD_REQUEST) {
    return { refusal: RESET_REFUSALS.PASSWORD_REJECTED };
  }
  // PLANNER-PALM-UAT-2026-11-03-001 U1: a validation refusal is read by the FIELD it names, never as a plain failure
  // (the reset that posted an empty company read "Something went wrong" on every tenant sign-in page).
  const field =
    status === UNPROCESSABLE && err.errorCode === VALIDATION_ERROR
      ? firstRefusedField(err.errorDetails)
      : null;
  if (field === RESET_TOKEN_FIELD) return { refusal: RESET_REFUSALS.SESSION_EXPIRED, backToEmail: true };
  if (field) return { refusal: RESET_REFUSALS.INVALID_FIELD, field };
  return { refusal: RESET_REFUSALS.FAILED };
}

export default classifyResetRefusal;
